import { useEffect, useState } from "react";
import { ArrowUpRight } from "lucide-react";
import Eyebrow from "./Eyebrow";

// Same-origin "/api" on Cloudflare; Emergent still sets REACT_APP_BACKEND_URL.
const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;
const BLOG_URL = "https://afisch.blog";
const PLACEHOLDER_COUNT = 3;

const formatDate = (iso) =>
  new Date(iso).toLocaleDateString("de-CH", { day: "numeric", month: "long", year: "numeric" });

// Posts load in the browser only, so the prerendered HTML shows the heading,
// placeholders and the blog link. If the feed is unavailable, only the link remains.
const BlogSection = () => {
  const [posts, setPosts] = useState(null);

  useEffect(() => {
    let cancelled = false;
    fetch(`${API}/blog`)
      .then((resp) => (resp.ok ? resp.json() : { posts: [] }))
      .then((data) => { if (!cancelled) setPosts(Array.isArray(data.posts) ? data.posts : []); })
      .catch(() => { if (!cancelled) setPosts([]); });
    return () => { cancelled = true; };
  }, []);

  return (
    <section id="blog" className="border-b py-16 sm:py-20 lg:py-28">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-10 space-y-10 lg:space-y-14">

        <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div className="space-y-2">
            <Eyebrow>Blog</Eyebrow>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight">
              Neueste Beiträge
            </h2>
            <p className="text-muted-foreground text-base max-w-2xl leading-relaxed pt-1">
              Was ich zuletzt auf meinem Blog afisch.blog geschrieben habe.
            </p>
          </div>
          <a
            href={BLOG_URL}
            target="_blank"
            rel="noopener"
            data-testid="blog-link"
            className="inline-flex w-fit items-center gap-1.5 rounded-lg border bg-card px-4 py-2
                       text-sm font-medium shadow-sm transition-colors hover:text-accent hover:border-accent/50"
          >
            Zum Blog
            <ArrowUpRight className="size-4" strokeWidth={1.8} />
          </a>
        </div>

        {posts === null && (
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3" aria-hidden="true">
            {Array.from({ length: PLACEHOLDER_COUNT }, (_, i) => (
              <div key={i} className="rounded-2xl border bg-card shadow-sm overflow-hidden animate-pulse">
                <div className="aspect-[16/9] bg-muted" />
                <div className="p-6 space-y-3">
                  <div className="h-3 w-24 rounded bg-muted" />
                  <div className="h-5 w-3/4 rounded bg-muted" />
                  <div className="h-3 w-full rounded bg-muted" />
                  <div className="h-3 w-5/6 rounded bg-muted" />
                </div>
              </div>
            ))}
          </div>
        )}

        {posts && posts.length > 0 && (
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="blog-grid">
            {posts.map((post, i) => (
              <a
                key={post.url}
                href={post.url}
                target="_blank"
                rel="noopener"
                data-testid={`blog-card-${i}`}
                className="group rounded-2xl border bg-card shadow-sm overflow-hidden flex flex-col
                           transition-colors hover:border-accent/50"
              >
                <div className="aspect-[16/9] bg-muted overflow-hidden">
                  {post.image && (
                    <img
                      src={post.image}
                      alt=""
                      loading="lazy"
                      decoding="async"
                      onError={(e) => { e.currentTarget.style.display = "none"; }}
                      className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.03]"
                    />
                  )}
                </div>
                <div className="p-6 flex flex-col gap-3 flex-1">
                  {post.date && (
                    <time dateTime={post.date} className="text-xs text-muted-foreground">
                      {formatDate(post.date)}
                    </time>
                  )}
                  <h3 className="text-lg font-semibold leading-snug group-hover:text-accent transition-colors">
                    {post.title}
                  </h3>
                  {post.excerpt && (
                    <p className="text-sm text-foreground/80 leading-relaxed flex-1">{post.excerpt}</p>
                  )}
                  <span className="inline-flex items-center gap-1 text-sm font-medium text-accent pt-1">
                    Weiterlesen
                    <ArrowUpRight className="size-4" strokeWidth={1.8} />
                  </span>
                </div>
              </a>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default BlogSection;
